/**
 * @fileoverview Defines `Voyage3Lite` and `Voyage3Code` embedding models, which implement `EmbeddingModelNeedingKeys` and use `voyageEmbedBatch` to interact with the Voyage AI API. Exports `embedVoyage` to embed lists of strings using these models, and `rerankVoyage` to rerank documents. Also defines `VoyageRerank2` and `VoyageRerank2Lite` for reranking.
 */

import { VoyageAIClient } from 'voyageai'
import { EnvKeyProvider, KeyProvider } from './api_keys'
import { EmbeddedDocument, EmbeddingModelNeedingKeys, EmbeddingResult } from './llm_types'
import { time } from '../util/timing'
import { trackCost } from '../util/usage_tracker'
import { debug } from '../util/logging'
import { batch } from '@iterator/shared-common/src/batch'
import { traced } from 'braintrust'

type VoyageModelName = 'voyage-3-lite' | 'voyage-code-3'

export const Voyage3Lite: EmbeddingModelNeedingKeys = {
  name: 'voyage-3-lite',
  dimensions: 512,
  tokenCost: 0.02 / 1000000,
  embed: (strs: string[], settings: KeyProvider) =>
    voyageEmbedBatch('voyage-3-lite', strs, settings),
}

export const Voyage3Code: EmbeddingModelNeedingKeys = {
  name: 'voyage-code-3',
  dimensions: 1024, // Default. can be 256, 512 or 2048
  tokenCost: 0.18 / 1000000,
  embed: (strs: string[], settings: KeyProvider) =>
    voyageEmbedBatch('voyage-code-3', strs, settings),
}

export async function voyageEmbedBatch(
  model: VoyageModelName,
  strs: string[],
  settings: KeyProvider,
): Promise<{ results: EmbeddingResult[]; usage?: { totalTokens?: number } }> {
  const client = new VoyageAIClient({
    apiKey: settings.get('VOYAGE_API_KEY'),
  })

  const results = await client.embed({
    input: strs,
    model: model,
  })

  return {
    results:
      results.data?.map((r) => ({ index: r.index ?? 0, embedding: r.embedding ?? [] })) ?? [],
    usage: results.usage,
  }
}

export async function embedVoyage(
  strs: string[],
  options: {
    batchSize: number
    model: EmbeddingModelNeedingKeys
    settings: KeyProvider
  } = {
    batchSize: 10,
    model: Voyage3Lite,
    settings: EnvKeyProvider,
  },
): Promise<EmbeddedDocument[]> {
  debug(
    `Embedding ${strs.length} strings, total size ${(strs.reduce((acc, s) => acc + s.length, 0) / (1024 * 1024)).toFixed(2)}MB`,
  )
  const results: EmbeddedDocument[] = []
  let totalTime = 0
  const batchOfDocuments = batch(strs, options.batchSize)

  for (const batch of batchOfDocuments) {
    const batchResult = await time('embedBatch', async () =>
      options.model.embed(batch, options.settings),
    )
    debug(
      `Embedded batch of ${batch.length} strings in ${batchResult.timing.durationMs}ms. Usage: ${batchResult.usage?.totalTokens} tokens`,
    )
    if (batchResult.usage?.totalTokens) {
      trackCost(
        'embedBatch',
        options.model.name,
        batchResult.usage.totalTokens * options.model.tokenCost,
      )
    }
    totalTime += batchResult.timing.durationMs
    results.push(
      ...batchResult.results.map((r) => ({
        str: batch[r.index!]!,
        embedding: r.embedding!,
      })),
    )
  }
  debug(`Embedding ${strs.length} strings took ${totalTime}ms.`)
  return results
}

export const VoyageRerank2: EmbeddingModelNeedingKeys = {
  name: 'rerank-2',
  tokenCost: 0.05 / 1000000,
  dimensions: 0, // Not applicable for rerankers
  embed: () => {
    throw new Error('Rerankers do not support embedding')
  },
}

export const VoyageRerank2Lite: EmbeddingModelNeedingKeys = {
  name: 'rerank-2-lite',
  tokenCost: 0.02 / 1000000,
  dimensions: 0,
  embed: () => {
    throw new Error('Rerankers do not support embedding')
  },
}

export async function rerankVoyage(
  query: string,
  documents: string[],
  options: {
    model?: 'rerank-2' | 'rerank-2-lite'
    settings: KeyProvider
    topK?: number
  } = {
    model: 'rerank-2-lite',
    settings: EnvKeyProvider,
    topK: 10,
  },
): Promise<{ index: number; relevanceScore: number }[]> {
  return traced(
    async (span) => {
      const client = new VoyageAIClient({
        apiKey: options.settings.get('VOYAGE_API_KEY'),
      })

      const results = await time('rerank', async () =>
        client.rerank({
          query,
          documents,
          model: options.model || 'rerank-2-lite',
          topK: options.topK,
        }),
      )

      if (results.usage?.totalTokens) {
        const model = options.model === 'rerank-2' ? VoyageRerank2 : VoyageRerank2Lite
        trackCost('rerank', model.name, results.usage.totalTokens * model.tokenCost)
      }

      span.log({
        input: {
          query,
          documents,
          topK: options.topK,
        },
        output: results.data,
        metrics: {
          total_tokens: results.usage?.totalTokens,
        },
        metadata: {
          model: options.model,
        },
      })

      return (
        results.data
          ?.filter((r) => r.index !== undefined && r.relevanceScore !== undefined)
          .map((r) => ({ index: r.index!, relevanceScore: r.relevanceScore! })) ?? []
      )
    },
    {
      name: 'rerankVoyage',
    },
  )
}
